import { errorMessage, STRAVA_SCOPE_ERROR_COPY } from "@atlas/shared";
import type { SocialSignInInput } from "@atlas/shared";
import { useState } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { login, socialSignIn } from "../../api/auth";
import { ConnectWithStravaButton, StravaConsentNote } from "../../components/StravaBrand";
import { SOCIAL_SIGN_IN_AVAILABLE, SocialSignInButtons } from "../../components/SocialSignInButtons";
import { useStravaConnectionsFull } from "../../hooks/useFeatureFlags";

function FieldGroup({ children }: { children: React.ReactNode }) {
  return <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>{children}</div>;
}

function Label({ htmlFor, children }: { htmlFor: string; children: React.ReactNode }) {
  return (
    <label htmlFor={htmlFor} style={{
      fontSize: "var(--text-sm)",
      fontWeight: 600,
      color: "var(--text-secondary)",
      fontFamily: "var(--font-sans)",
    }}>
      {children}
    </label>
  );
}

function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      style={{
        width: "100%",
        height: "var(--control-md)",
        padding: "0 12px",
        background: "var(--surface-sunken)",
        border: "1px solid var(--border-default)",
        borderRadius: "var(--radius-md)",
        color: "var(--text-primary)",
        fontSize: "var(--text-base)",
        fontFamily: "var(--font-sans)",
        outline: "none",
        boxSizing: "border-box",
        ...props.style,
      }}
    />
  );
}

const STRAVA_OAUTH_ERRORS: Record<string, string> = {
  strava_full:
    "Strava connections are temporarily full. Sign in with email, or import by GPX for now.",
  strava_denied: "Strava access was declined.",
  strava_token_failed: "Strava sign-in failed. Try again.",
  // No account was made: the grant could not read a single activity. The
  // Connect with Strava button above is the "try again".
  strava_scope: STRAVA_SCOPE_ERROR_COPY,
};

export default function LoginPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // The API's Strava start/callback redirects here with ?error=<kind> when
  // it can't proceed. Read once on mount.
  const [oauthErrorKind] = useState<string | null>(() => {
    try {
      return new URLSearchParams(window.location.search).get("error");
    } catch {
      return null;
    }
  });
  const oauthError = oauthErrorKind
    ? (STRAVA_OAUTH_ERRORS[oauthErrorKind] ?? "Strava sign-in failed. Try again.")
    : null;

  // Same-site app paths only ("/route-analyser"), never "//host", a backslash host or a URL.
  const { next } = useSearch({ from: "/_auth/login" });
  const returnTo = next && /^\/(?![/\\])/.test(next) ? next : null;

  const mutation = useMutation({
    mutationFn: login,
    onSuccess: (data) => {
      qc.setQueryData(["me"], data);
      void navigate({ to: returnTo ?? "/dashboard" });
    },
  });

  // Apple / Google (ATL-130). A first-time sign-in from this page creates
  // the account; the dashboard's getting-started strip takes it from there.
  const [socialError, setSocialError] = useState<string | null>(null);
  const socialMutation = useMutation({
    mutationFn: (input: SocialSignInInput) => socialSignIn(input),
    onSuccess: (data) => {
      qc.setQueryData(["me"], { user: data.user });
      void navigate({ to: returnTo ?? "/dashboard" });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate({ email, password });
  };

  // After a refused grant, make Strava show its consent screen again (it may
  // otherwise skip straight back with the same missing permission).
  const stravaOAuthUrl = `${import.meta.env["VITE_API_URL"] ?? "/api"}/auth/strava${oauthErrorKind === "strava_scope" ? "?force=1" : ""}`;
  const stravaFull = useStravaConnectionsFull();

  return (
    <div style={{
      background: "var(--surface-card)",
      border: "1px solid var(--border-subtle)",
      borderRadius: "var(--radius-xl)",
      padding: "var(--space-8)",
      boxShadow: "var(--shadow-md), var(--ring-top)",
    }}>
      <div style={{ marginBottom: "var(--space-7)" }}>
        <div className="eyebrow" style={{ marginBottom: "var(--space-2)" }}>
          Welcome back
        </div>
        <h1 style={{ font: "var(--type-h2)", color: "var(--text-primary)" }}>
          Sign in to Atlas
        </h1>
      </div>

      {(SOCIAL_SIGN_IN_AVAILABLE || !stravaFull) && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 16 }}>
          <SocialSignInButtons
            intent="signin"
            pending={socialMutation.isPending}
            onCredential={(input) => {
              setSocialError(null);
              socialMutation.mutate(input);
            }}
            onError={setSocialError}
          />
          {!stravaFull && (
            <ConnectWithStravaButton href={stravaOAuthUrl} height={44} fullWidth />
          )}
          {(socialMutation.error || socialError) && (
            <div style={{
              padding: "10px 12px",
              background: "var(--danger-soft)",
              border: "1px solid rgba(224, 80, 63, 0.4)",
              borderRadius: "var(--radius-md)",
              fontSize: "var(--text-sm)",
              color: "var(--coral-300)",
            }}>
              {socialMutation.error
                ? errorMessage(socialMutation.error, "Couldn't sign you in. Try again.")
                : socialError}
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ flex: 1, height: 1, background: "var(--border-subtle)" }} />
            <span className="eyebrow">or with email</span>
            <div style={{ flex: 1, height: 1, background: "var(--border-subtle)" }} />
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <FieldGroup>
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
          />
        </FieldGroup>

        <FieldGroup>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Label htmlFor="password">Password</Label>
            <Link
              to="/forgot-password"
              style={{ fontSize: "var(--text-sm)", color: "var(--text-accent)", fontWeight: 600 }}
            >
              Forgot password?
            </Link>
          </div>
          <Input
            id="password"
            type="password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
          />
        </FieldGroup>

        {(mutation.error || oauthError) && (
          <div style={{
            padding: "10px 12px",
            background: "var(--danger-soft)",
            border: "1px solid rgba(224, 80, 63, 0.4)",
            borderRadius: "var(--radius-md)",
            fontSize: "var(--text-sm)",
            color: "var(--coral-300)",
          }}>
            {mutation.error ? errorMessage(mutation.error, "Couldn't sign you in. Try again.") : oauthError}
          </div>
        )}

        <button
          type="submit"
          disabled={mutation.isPending}
          style={{
            width: "100%",
            height: "var(--control-lg)",
            background: mutation.isPending ? "var(--accent-press)" : "var(--accent)",
            color: "var(--text-on-gold)",
            border: "none",
            borderRadius: "var(--radius-md)",
            fontFamily: "var(--font-sans)",
            fontSize: "var(--text-base)",
            fontWeight: 600,
            cursor: mutation.isPending ? "not-allowed" : "pointer",
            transition: "background 0.15s ease",
          }}
        >
          {mutation.isPending ? "Signing in…" : "Sign in"}
        </button>

      </form>

      {!stravaFull && <StravaConsentNote compact style={{ marginTop: "var(--space-5)" }} />}

      <p style={{
        marginTop: "var(--space-6)",
        textAlign: "center",
        fontSize: "var(--text-sm)",
        color: "var(--text-muted)",
        fontFamily: "var(--font-sans)",
      }}>
        No account?{" "}
        <Link
          to="/signup"
          style={{ color: "var(--text-accent)", fontWeight: 600 }}
        >
          Create one →
        </Link>
      </p>
    </div>
  );
}
